import React, { useEffect, useState, useMemo } from "react";
import {
  listCompanies,
  updateCompany,
  deleteCompany,
  createFactory,
  listFactories,
} from "../../api/users/companies";
import SidePanel from "../../components/common/layout/SidePanel";
import CompanyList from "../../components/company/CompanyList";
import FactoryModal from "../../components/company/FactoryModal";
import ConfirmDialog from "../../components/common/ui/ConfirmModal";

const EMPTY_COMPANY = {
  name: "",
  registration_no: "",
  address: "",
  detail_address: "",
  extra_address: "",
  phone: "",
  email: "",
};

// 공장 행(회사 등록 시 함께 등록)
const EMPTY_FACTORY_ROW = {
  name: "",
  address: "",
  detail_address: "",
  extra_address: "",
};

const User_company = () => {
  const [companies, setCompanies] = useState([]);

  // 회사 등록 폼
  const [newCompany, setNewCompany] = useState({ ...EMPTY_COMPANY });
  const [hasFactories, setHasFactories] = useState(false); // 공장 있음 체크
  const [factoryRows, setFactoryRows] = useState([{ ...EMPTY_FACTORY_ROW }]);

  // 편집 상태(회사)
  const [editCompanyId, setEditCompanyId] = useState(null);
  const [editData, setEditData] = useState({ ...EMPTY_COMPANY });

  // 회사 수정 사이드 패널
  const [isEditMode, setIsEditMode] = useState(false);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // { id, ... }
  const [editForm, setEditForm] = useState({ ...EMPTY_COMPANY });
  const [savingEdit, setSavingEdit] = useState(false);

  // 상세 → 수정 전환
  const handleEnterEditMode = () => setIsEditMode(true);
  const handleCancelEditMode = () => setIsEditMode(false);

  // 공장 관리 모달 상태
  const [factoryModalOpen, setFactoryModalOpen] = useState(false);
  const [factoryCompany, setFactoryCompany] = useState(null); // { id, name }
  const [factoryList, setFactoryList] = useState([]);

  // 확인(작성내용 삭제 안내) 모달 상태
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingFactoryCompany, setPendingFactoryCompany] = useState(null); // 확인 후 열 모달 대상

  const [search, setSearch] = useState(""); // 검색

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async (query = "") => {
    try {
      const res = await listCompanies({ search: query });
      const data = res?.data ?? res;
      console.log("회사 목록 데이터:", data);
      const rows = Array.isArray(data) ? data : data?.results ?? [];
      setCompanies(rows);
    } catch (error) {
      console.error("회사 목록 불러오기 실패:", error);
    }
  };

  // 검색 핸들러
  const handleSearch = async (e) => {
    e.preventDefault();
    await loadCompanies(search);
  };

  const handleUpdateCompany = async (data) => {
    try {
      const id = data.id;
      await updateCompany(id, data);
      setEditCompanyId(null);
      await loadCompanies();
      alert("회사 정보가 수정되었습니다.");
    } catch (error) {
      console.error("회사 수정 실패:", error);
      alert(
        "수정 실패\n" + JSON.stringify(error?.response?.data || {}, null, 2)
      );
    }
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      await deleteCompany(id);
      await loadCompanies();
      alert("회사가 삭제되었습니다.");
      window.location.reload();
    } catch (error) {
      console.error("회사 삭제 실패:", error);
      alert(
        "삭제 실패\n" + JSON.stringify(error?.response?.data || {}, null, 2)
      );
    }
  };

  // --------- 신규 회사 폼 수정 여부 판단 ---------
  const isEmpty = (v) =>
    v === null || v === undefined || (typeof v === "string" && v.trim() === "");

  const isCompanyFormDirty = useMemo(() => {
    // 1) 신규 회사 폼 내용이 비어 있지 않으면 dirty
    const companyDirty = Object.keys(EMPTY_COMPANY).some(
      (k) => !isEmpty(newCompany[k])
    );

    // 2) 공장 섹션 체크/행 내용이 있으면 dirty
    const rowNotEmpty = (row) =>
      ["name", "address", "detail_address", "extra_address"].some(
        (k) => !isEmpty(row[k])
      );
    const factoriesDirty =
      hasFactories ||
      factoryRows.length > 1 ||
      (factoryRows.length === 1 && rowNotEmpty(factoryRows[0]));

    // 3) 목록 인라인 편집 중인 경우도 dirty로 간주(작성중인 변경 보존)
    const editingDirty = editCompanyId !== null;

    return companyDirty || factoriesDirty || editingDirty;
  }, [newCompany, hasFactories, factoryRows, editCompanyId]);

  // --------- 모달 실제 오픈 로직 ---------
  const actuallyOpenFactoryModal = async (company) => {
    setFactoryCompany({
      id: company.id,
      name: company.name,
      address: company.address,
      extra_address: company.extra_address,
      detail_address: company.detail_address,
    });
    setFactoryModalOpen(true);
    try {
      const res = await listFactories({ company: company.id }); // 백엔드에 company 필터가 있다고 가정
      const data = res?.data ?? res;
      const rows = Array.isArray(data) ? data : data?.results ?? [];
      setFactoryList(rows);
    } catch (e) {
      console.error("공장 목록 불러오기 실패:", e);
      setFactoryList([]);
    }
  };

  // --------- 공장 모달 열기 (확인 모달 포함) ---------
  const openFactoryModal = async (company) => {
    if (isCompanyFormDirty) {
      // 작성 내용이 있으면 확인 모달 먼저 띄움
      setPendingFactoryCompany(company);
      setConfirmOpen(true);
      return;
    }
    // 작성 내용 없으면 바로 오픈
    setFactoryRows([{ ...EMPTY_FACTORY_ROW }]); // Tmap 초기화
    setHasFactories(false);
    await actuallyOpenFactoryModal(company);
  };

  const handleConfirmDeleteDraftAndOpen = async () => {
    // 신규 회사 폼/공장 행 작성 내용 삭제
    setNewCompany({ ...EMPTY_COMPANY });
    setHasFactories(false);
    setFactoryRows([{ ...EMPTY_FACTORY_ROW }]);
    setEditCompanyId(null); // 인라인 편집 취소(선택)

    // 모달 열기
    if (pendingFactoryCompany) {
      await actuallyOpenFactoryModal(pendingFactoryCompany);
    }
    setPendingFactoryCompany(null);
    setConfirmOpen(false);
  };

  const handleCancelKeepDraft = () => {
    // 모달 열지 않고 작성 내용 유지
    setPendingFactoryCompany(null);
    setConfirmOpen(false);
  };

  const closeFactoryModal = () => {
    setFactoryModalOpen(false);
    setFactoryCompany(null);
    setFactoryList([]);
  };

  // const startEditCompany = (company) => {
  //   setEditCompanyId(company.id);
  //   setEditData({
  //     name: company.name ?? "",
  //     registration_no: company.registration_no ?? "",
  //     address: company.address ?? "",
  //     detail_address: company.detail_address ?? "",
  //     extra_address: company.extra_address ?? "",
  //     phone: company.phone ?? "",
  //     email: company.email ?? "",
  //   });
  // };
  // -----------------  회사 수정 / 상세보기 패널 로직 -----------------
  const openEditPanel = (company, options = {}) => {
    setEditTarget(company);
    setEditForm({ ...company });
    setEditPanelOpen(true);
    setIsEditMode(!!options.editMode); // editMode 옵션에 따라 수정모드로 열기
  };

  const openViewPanel = (company) => {
    openEditPanel(company, { editMode: false }); // 상세보기 모드로
  };

  const closeEditPanel = () => {
    if (savingEdit) return; // 저장 중에는 닫기 방지 (원하시면 삭제)
    setEditPanelOpen(false);
    setEditTarget(null);
    setEditForm({ ...EMPTY_COMPANY });
  };

  const submitEdit = async () => {
    if (!editTarget) return;
    setSavingEdit(true);
    try {
      await handleUpdateCompany({ id: editTarget.id, ...editForm });
      setEditPanelOpen(false);
      setEditTarget(null);
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4">회사 관리</h2>
      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          type="text"
          placeholder="회사명, 사업자번호 검색"
          className="border rounded px-3 py-1 text-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="submit"
          className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
        >
          검색
        </button>
      </form>

      {/* 회사 목록 */}
      <CompanyList
        companies={companies}
        editCompanyId={editCompanyId}
        editData={editData}
        setEditData={setEditData}
        setEditCompanyId={setEditCompanyId}
        setNewCompany={setNewCompany}
        onOpenFactoryModal={openFactoryModal}
        onEdit={(company) => openEditPanel(company, { editMode: true })}
        onView={openViewPanel}
      />

      {/* 공장 관리 모달 */}
      {factoryModalOpen && (
        <FactoryModal
          company={factoryCompany}
          factoryList={factoryList}
          onClose={closeFactoryModal}
          reloadFactoryList={async () => {
            try {
              const res = await listFactories({ company: factoryCompany.id });
              const data = res?.data ?? res;
              setFactoryList(Array.isArray(data) ? data : data?.results ?? []);
            } catch (e) {
              console.error("공장 목록 새로고침 실패:", e);
            }
          }}
        />
      )}

      {/* 작성내용 삭제 경고 Confirm */}
      <ConfirmDialog
        open={confirmOpen}
        title="작성 중인 내용이 있습니다"
        description="공장 관리 모달을 열면 현재 작성 중인 회사/공장 입력값이 삭제됩니다. 계속하시겠습니까?"
        confirmText="삭제하고 열기"
        cancelText="취소"
        onConfirm={handleConfirmDeleteDraftAndOpen}
        onCancel={handleCancelKeepDraft}
        onClose={handleCancelKeepDraft}
      />

      {/* 회사 수정 사이드패널 */}
      <SidePanel
        isOpen={editPanelOpen}
        onClose={closeEditPanel}
        title={
          editTarget
            ? isEditMode
              ? `회사 수정 – ${editTarget.name}`
              : `회사 상세보기 – ${editTarget.name}`
            : "회사 정보"
        }
        width={640} // px 숫자 or "28rem" 등 문자열 가능
        side="right" // 기본 right
        headerActions={null}
        footer={
          isEditMode ? (
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded-lg border hover:bg-gray-50"
                onClick={handleCancelEditMode}
                disabled={savingEdit}
              >
                취소
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={submitEdit}
                disabled={savingEdit}
              >
                {savingEdit ? "저장 중..." : "저장"}
              </button>
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded-lg border hover:bg-gray-50"
                onClick={closeEditPanel}
              >
                닫기
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                onClick={handleEnterEditMode}
              >
                수정
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                onClick={() => handleDeleteCompany(editTarget.id)}
              >
                삭제
              </button>
            </div>
          )
        }
      >
        {isEditMode ? (
          // ---------------- 수정 폼 ----------------
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                회사명 *
              </label>
              <input
                className="w-full border rounded px-3 py-2"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm((v) => ({ ...v, name: e.target.value }))
                }
                placeholder="회사명"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  사업자등록번호
                </label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={editForm.registration_no}
                  onChange={(e) =>
                    setEditForm((v) => ({
                      ...v,
                      registration_no: e.target.value,
                    }))
                  }
                  placeholder="숫자/하이픈 포함"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  전화번호
                </label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={editForm.phone}
                  onChange={(e) =>
                    setEditForm((v) => ({ ...v, phone: e.target.value }))
                  }
                  placeholder="예: 02-1234-5678"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">이메일</label>
              <input
                className="w-full border rounded px-3 py-2"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm((v) => ({ ...v, email: e.target.value }))
                }
                placeholder="info@example.com"
                type="email"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">주소</label>
              <input
                className="w-full border rounded px-3 py-2"
                value={editForm.address}
                onChange={(e) =>
                  setEditForm((v) => ({ ...v, address: e.target.value }))
                }
                placeholder="도로명 주소"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  참고주소
                </label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={editForm.extra_address}
                  onChange={(e) =>
                    setEditForm((v) => ({
                      ...v,
                      extra_address: e.target.value,
                    }))
                  }
                  placeholder="(동/리 등)"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  상세주소
                </label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={editForm.detail_address}
                  onChange={(e) =>
                    setEditForm((v) => ({
                      ...v,
                      detail_address: e.target.value,
                    }))
                  }
                  placeholder="층/호 등"
                />
              </div>
            </div>
          </div>
        ) : (
          // ---------------- 상세 보기 ----------------
          <div className="space-y-3 text-sm">
            <p>
              <strong>회사명:</strong> {editTarget?.name}
            </p>
            <p>
              <strong>사업자등록번호:</strong>{" "}
              {editTarget?.registration_no || "-"}
            </p>
            <p>
              <strong>전화번호:</strong> {editTarget?.phone || "-"}
            </p>
            <p>
              <strong>이메일:</strong> {editTarget?.email || "-"}
            </p>
            <p>
              <strong>주소:</strong> {editTarget?.address}{" "}
              {editTarget?.detail_address}
            </p>
            <p>
              <strong>상세주소:</strong> {editTarget?.detail_address}
            </p>
            <p>
              <strong>참고주소:</strong> {editTarget?.extra_address}
            </p>
            <p className="text-gray-500">
              <strong>생성일시:</strong>{" "}
              {editTarget?.created_at
                ? new Date(editTarget.created_at).toLocaleString()
                : "-"}
            </p>
            <p className="text-gray-500">
              <strong>수정일시:</strong>{" "}
              {editTarget?.updated_at
                ? new Date(editTarget.updated_at).toLocaleString()
                : "-"}
            </p>
          </div>
        )}
      </SidePanel>
    </div>
  );
};

export default User_company;
